import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { FormStep } from "../FormStep/FormStep"
import { csrfTokenGet } from "../../Utility/WoodGreenPaymentsApi"
import {
  getCookie,
  dataLayerPushCheckoutEvent,
  dataLayerPushTransaction,
  elementCheckVisibility,
  sessionStateGet,
  dataLayerTransactionEventBuild,
  setDonationReminderCookie,
  scrollToTop
} from "../../Utility/Utility"
import { PaymentStateManager } from "../../Utility/PaymentStateManager"

import './Form.scss'

const hashPrefix = "#step-"

/**
 * Primary UI component for user interaction
 */
export const Form = ({ giftaidRate, landingPage, loqateKey, steps, thankYouPage, staticText, expectedValues, donationFeeRate }) => {
  const [csrfTokenError, csrfTokenErrorSet]     = useState(false)
  const [currentStepIndex, currentStepIndexSet] = useState(0)
  const [stateManager, stateManagerSet]         = useState(null)
  const [validationHash, validationHashSet]     = useState('')

  /**
   * The valid parameters to be sent to the thank you page.
   *
   * @type {string[]}
   */
  const validParams = ['appeal_id', 'payment_type', 'payment_amount', 'giftaid']

  /**
   * Set the current step.
   *
   * @param {string} direction
   *   The direction to move.
   */
  const currentStepSet = direction => {
    let nextStep = currentStepIndex

    if (direction === 'forward') {
      nextStep = stepsGetNext() === null ? nextStep : stepsGetNext()
    }

    if (direction === 'back') {
      nextStep = stepsGetPrevious() === null ? nextStep : stepsGetPrevious()
    }

    currentStepIndexSet(nextStep)
  }

  /**
   * Get the current step from the given URL hash.
   *
   * @param {string} stepHash
   *   The URL hash.
   *
   * @returns {Promise<number>}
   */
  const currentStepGetFromUrl = async (stepHash) => {
    if (stepHash) {
      const step = Number(stepHash.replace(hashPrefix, ""))

      if (!isNaN(step)) {
        if (step < totalSteps) {
          return step
        }
      }
    }

    return 0
  }


  /**
   * Handle the pop state event.
   */
  const handlePopState = () => {
    currentStepGetFromUrl(window.location.hash)
      .then((step) => {
        currentStepIndexSet(step)
      })
      .catch(() => console.error("Error"))
  }

  /**
   * Update the validation hash
   */
  const handlerValidationChange = () => {
    validationHashSet(`${Date.now()}`)
  }

  /**
   * Handle the next action.
   *
   * @param {string} stepId
   *   The step ID.
   * @param {string} stepOption
   *   The step option
   */
  const handlerTrackCheckoutStep = (stepId, stepOption) => {
    if (window['dataLayer']) {
      dataLayerPushCheckoutEvent(window['dataLayer'], stepId, stepOption, stateManager.valuesGet(), giftaidRate)
    }
  }

  /**
   * Handle the next action.
   *
   * @param {string} direction
   *   The direction to move.
   */
  const handlerNextAction = direction => {
    if (direction === 'forward' && stepsGetNext() === null) {
        console.log("navigate to thank you")
        localStorage.clear()
        setDonationReminderCookie({}, true)
        navigateToThankYouPage()
    }
    else {
      currentStepSet(direction)
    }
  }

  /**
   * Get the next step.
   *
   * @returns {number|null}
   *   The next step or null if there is no next step.
   */
  const stepsGetNext = () => {
    for (let i = currentStepIndex + 1; i < steps.length; i++) {
      if (elementCheckVisibility(steps[i], currentValues, currentOrderIsGift)) {
        return i
      }
    }

    return null
  }

  /**
   * Get the next step.
   *
   * @returns {null|{}}
   *   The next step or null if there is no next step.
   */
  const stepsGetPrevious = () => {
    for (let i = currentStepIndex - 1; i >= 0; i--) {
      if (elementCheckVisibility(steps[i], currentValues, currentOrderIsGift)) {
        return i
      }
    }

    return null
  }

  /**
   * Trigger the navigation to the Thank you page.
   */
  const navigateToThankYouPage = () => {
    localStorage.removeItem('sponsorshipData')
    window.location.href = thankYouPage + (thankYouPage.includes('?') ? '&' : '?') + Object.keys(currentValues)
      .filter(key => validParams.includes(key)).map(key => {
        let queryParam = currentValues[key];

        // Redirect to thank you page
        if (key === 'payment_amount' && donationFeeRate && currentValues['cover_donation_fee'] === 'Y') {
          queryParam = (parseFloat(currentValues.payment_amount) * donationFeeRate).toFixed(2).replace('.00', '')
        }
        return `${key}=${queryParam}`
      }).join('&')
  }

  /**
   * Trigger the navigation to the landing page.
   */
  const navigateToLandingPage  = () => {
    if (landingPage) {
      window.location.href = landingPage
    }
  }

  const urlExtractTrackingParams = paymentStateManager => {
    const urlParams = new URLSearchParams(window.location.search)

    if (urlParams.get('utm_campaign')) {
      paymentStateManager.valueUpdate('utm_campaign', urlParams.get('utm_campaign'))
    }
    else if (getCookie('utm_campaign')) {
      paymentStateManager.valueUpdate('utm_campaign', getCookie('utm_campaign'))
    }

    if (urlParams.get('utm_medium')) {
      paymentStateManager.valueUpdate('utm_medium', urlParams.get('utm_medium'))
    }
    else if (getCookie('utm_medium')) {
      paymentStateManager.valueUpdate('utm_medium', getCookie('utm_medium'))
    }

    if (urlParams.get('utm_source')) {
      paymentStateManager.valueUpdate('utm_source', urlParams.get('utm_source'))
    }
    else if (getCookie('utm_source')) {
      paymentStateManager.valueUpdate('utm_source', getCookie('utm_source'))
    }

    console.log(paymentStateManager.userValues)
  }

  /**
   * The current step.
   *
   * @type {{}|null}
   */
  const currentStep = steps[currentStepIndex] ? steps[currentStepIndex] : null

  /**
   * A boolean indicating if the payment has completed.
   *
   * @type {boolean}
   */
  const paymentHasCompleted = stateManager ? stateManager.paymentHasCompleted() : null

  /**
   * The currently stored values.
   *
   * @type {{}}
   */
  const currentValues = stateManager ? stateManager.valuesGet() : {}

  /**
   * A boolean indicating if the current order is a gift.
   *
   * @type {boolean}
   */
  const currentOrderIsGift = stateManager && stateManager.hasOrders() ? stateManager.orderItemAtIndexIsGift(stateManager.ordersGetCurrentIndex()) : false

  /**
   * The total number of visible steps.
   *
   * @type {number}
   */
  const totalSteps = steps.filter(step => elementCheckVisibility(step, currentValues, currentOrderIsGift)).length

  /**
   * Calculate the step index based on the steps that are visible.
   *
   * @type {number}
   */
  const visibleStepIndex = useMemo(() => {
    let visibleIndex = -1

    for (let i = 0; i <= currentStepIndex; i++) {
      if (elementCheckVisibility(steps[i], currentValues, currentOrderIsGift)) {
        visibleIndex++
      }
    }

    return visibleIndex

  }, [currentStepIndex])

  /**
   * Update the history state when the current state changes.
   */
  useEffect(() => {
    if (!isNaN(currentStepIndex)) {
      let historyPath = window.location.pathname

      if (window.location.search) {
        historyPath += `${window.location.search}`
      }

      history.pushState({}, null, `${historyPath}${hashPrefix}${currentStepIndex}`)
      scrollToTop()
    }
  }, [currentStepIndex])

  /**
   * Register / de-register the pop state listener
   */
  useEffect(() => {
    window.addEventListener("popstate", handlePopState)

    return () => {
      window.removeEventListener("popstate", handlePopState)
    }
  })

  /**
   * Update the donation reminder cookie on every form render.
   */
  useEffect(() => {
    if (stateManager && stateManager.userValues && typeof stateManager.userValues === 'object') {
      setDonationReminderCookie(stateManager.userValues, paymentHasCompleted)
    }
  })

  useEffect(() => {
    if(stateManager) {
      console.log('dataLayer', dataLayerTransactionEventBuild(stateManager.valuesGet(), giftaidRate, donationFeeRate))
    }

    if (paymentHasCompleted && window['dataLayer']) {
      dataLayerPushTransaction(window['dataLayer'], stateManager.valuesGet(), giftaidRate, donationFeeRate, navigateToThankYouPage)
    }
  }, [paymentHasCompleted])


  useEffect(() => {
    csrfTokenGet()
      .then(response => {
        const stateManagerInstance = new PaymentStateManager()
        stateManagerInstance.valuesSet(sessionStateGet())
        stateManagerInstance.valuesSet(JSON.parse(JSON.stringify(expectedValues)))
        stateManagerInstance.valueUpdate('csrf_token', response.csrf_token)
        urlExtractTrackingParams(stateManagerInstance)
        stateManagerSet(stateManagerInstance)
      })
      .catch(error => {
        console.error('error', error)
        csrfTokenErrorSet(true)
      })
  }, [])

  return (
    <div className={'o-form'}>
      {csrfTokenError && <div>CSRF token error</div> }

      {currentStep && stateManager && !csrfTokenError ?
        <FormStep
          canGoBack={!(currentStepIndex === 0)}
          giftaidRate={giftaidRate}
          donationFeeRate={donationFeeRate}
          isSubmitStep={stepsGetNext() === null}
          key={currentStepIndex}
          loqateKey={loqateKey}
          onNextAction={handlerNextAction}
          onValidate={handlerValidationChange}
          onNavigateToLandingPage={navigateToLandingPage}
          paymentHasCompleted={paymentHasCompleted}
          shouldScroll={!(currentStepIndex === 0)}
          staticText={staticText}
          validationHash={validationHash}
          values={currentValues}
          currentStepNumber={visibleStepIndex}
          totalSteps={totalSteps}
          stateManager={stateManager}
          onTrackCheckoutStep={handlerTrackCheckoutStep}
          {...currentStep}
        /> : null }
    </div>
  )
}

Form.propTypes = {
  steps         : PropTypes.array,
  expectedValues: PropTypes.object,
  thankYouPage  : PropTypes.string,
  landingPage   : PropTypes.string,
  loqateKey     : PropTypes.string,
  giftaidRate   : PropTypes.number,
  staticText    : PropTypes.object,
}
